import React, { useState, useEffect } from "react";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { Spin, Pagination } from "antd";
import dayjs from "dayjs";

const EnquiryActivityLog = ({ orgId, dealCategoryId }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs = [], loading = false, totalRecords = 0 } = useSelector((state) => state?.activityLog) || {};

  useEffect(() => {
    if (orgId) {
      dispatch(getActivityLog({ module: "ENQUIRY", entityId: orgId, page, limit, dealCategoryId }));
    }
  }, [dispatch, orgId, page, dealCategoryId]);

  const getActionConfig = (action) => {
    switch (action) {
      case "NEW_ENQUIRY":
      case "ENQUIRY_CREATED": return { label: "Onboarding", color: "#e6ffed", textColor: "#22c55e" };
      case "APPROVE_ENQUIRY":
      case "ENQUIRY_APPROVED": return { label: "Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "REJECT_ENQUIRY":
      case "ENQUIRY_REJECTED": return { label: "Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "COMPLETE_KYC": return { label: "KYC Completed", color: "#e6ffed", textColor: "#22c55e" };
      case "REVIEW_KYC": return { label: "KYC Reviewed", color: "#e6f7ff", textColor: "#1890ff" };
      case "QUESTIONNAIRE_SENT": return { label: "Questionnaire Sent", color: "#e6f7ff", textColor: "#1890ff" };
      case "QUESTIONNAIRE_RESPONSE": return { label: "Questionnaire Response", color: "#e6f7ff", textColor: "#1890ff" };
      case "ACTIVATE_USER": return { label: "User Activated", color: "#e6ffed", textColor: "#22c55e" };
      case "REJECT_USER": return { label: "User Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "REOPEN_ENQUIRY": return { label: "Enquiry Reopened", color: "#fffbe6", textColor: "#d4b106" };
      case "CREDIT_LIMIT_CREATED":
      case "CREDIT_LIMIT_ASSIGNED": return { label: "Credit Limit Assigned", color: "#fff2e8", textColor: "#fa541c" };
      case "CREDIT_LIMIT_APPROVED": return { label: "Credit Limit Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "CREDIT_LIMIT_REJECTED": return { label: "Limit Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "CREDIT_LIMIT_UPDATED":
      case "CREDIT_LIMIT_ENHANCED": return { label: "Limit Enhanced", color: "#e6f7ff", textColor: "#1890ff" };
      default: return { label: action.replace(/_/g, " "), color: "#e6f7ff", textColor: "#1890ff" };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const actionConfig = getActionConfig(log.action);
    const busName = log.payload?.busName;
    const busStr = busName ? ` for <strong>"${busName}"</strong>` : "";
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong>${busStr}`;
  };

  return (
    <div style={{ padding: "24px 0", maxWidth: "900px", margin: "0 auto", fontFamily: "Inter, sans-serif" }}>
      <div style={{ border: "1px solid #f0f0f0", borderRadius: "8px", overflow: "hidden", background: "#ffffff" }}>
        
        <div 
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            padding: "16px 24px", 
            background: "#fafafa", 
            borderBottom: isCollapsed ? "none" : "1px solid #f0f0f0",
            cursor: "pointer",
            userSelect: "none"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#262626" }}>Business Onboarding Activity</span>
            <span style={{ 
              background: "#e8e8e8", 
              color: "#595959", 
              fontSize: "12px", 
              fontWeight: "600", 
              padding: "2px 8px", 
              borderRadius: "4px" 
            }}>
              {totalRecords}
            </span>
          </div>
          <div>
            {isCollapsed ? <DownOutlined style={{ fontSize: "14px", color: "#595959" }} /> : <UpOutlined style={{ fontSize: "14px", color: "#595959" }} />}
          </div>
        </div>

        {!isCollapsed && (
          <div style={{ padding: "24px 24px 8px 24px" }}>
            <Spin spinning={loading}>
              {logs?.length === 0 ? (
                <div style={{ textAlign: "center", color: "#8c8c8c", padding: "20px 0" }}>No activity logs found.</div>
              ) : (
                logs?.map((log, index) => {
                  const isLast = index === logs.length - 1;
                  const actionConfig = getActionConfig(log.action);
                  const dateStr = dayjs(log.createdAt).format("DD MMM YYYY");
                  const timeStr = dayjs(log.createdAt).format("hh:mm A");

                  return (
                    <div key={log.logId || index} style={{ display: "flex", minHeight: "80px" }}>
                      
                      <div style={{ width: "90px", textAlign: "right", paddingRight: "20px", flexShrink: 0 }}>
                        <div style={{ fontSize: "12px", fontWeight: "500", color: "#262626" }}>{dateStr}</div>
                        <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "2px" }}>{timeStr}</div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "20px", flexShrink: 0 }}>
                        <div style={{ 
                          width: "16px", 
                          height: "16px", 
                          borderRadius: "50%", 
                          border: `3px solid ${actionConfig.textColor}`, 
                          background: "#ffffff", 
                          display: "flex", 
                          justifyContent: "center", 
                          alignItems: "center",
                          boxSizing: "border-box"
                        }}>
                          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: actionConfig.textColor }} />
                        </div>

                        {!isLast && (
                          <div style={{ 
                            width: "3px", 
                            background: actionConfig.textColor, 
                            flexGrow: 1, 
                            marginTop: "4px", 
                            marginBottom: "4px" 
                          }} />
                        )}
                      </div>

                      <div style={{ paddingLeft: "20px", paddingBottom: "24px", flexGrow: 1 }}>
                        <div 
                          style={{ fontSize: "13px", color: "#434343", lineHeight: "1.5" }}
                          dangerouslySetInnerHTML={{ __html: formatDesc(log) }}
                        />
                        
                        <div style={{ display: "flex", alignItems: "center", gap: "24px", marginTop: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Action:</span>
                            <span style={{ 
                              background: actionConfig.color, 
                              color: actionConfig.textColor, 
                              fontSize: "11px", 
                              fontWeight: "700", 
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              padding: "2px 8px", 
                              borderRadius: "4px" 
                            }}>
                              {actionConfig.label}
                            </span>
                          </div>
                        </div>

                        {log.payload && Object.keys(log.payload).length > 0 && (
                          <div style={{ 
                            marginTop: 10, 
                            padding: "10px 16px", 
                            background: "#fafafa", 
                            border: "1px solid #f0f0f0", 
                            borderRadius: "6px",
                            fontSize: "12px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.01)"
                          }}>
                            {Object.entries(log.payload)
                              .filter(([key]) => key !== "entityTypeId" && key !== "entityType" && key !== "businessTypeId" && key !== "businessType")
                              .map(([key, value]) => {
                                const displayKey = key === "dealCategoryId" ? "dealCategoryName" : key;
                                return (
                                  <div key={key} style={{ marginBottom: "6px" }}>
                                    <span style={{ fontWeight: "600", color: "#595959", textTransform: "capitalize" }}>{displayKey.replace(/([A-Z])/g, ' $1').trim()}:</span>{" "}
                                    <span style={{ color: "#262626" }}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>

                    </div>
                  );
                })
              )}
            </Spin>
            {totalRecords > limit && (
              <div style={{ textAlign: "right", marginTop: 10 }}>
                <Pagination
                  current={page}
                  total={totalRecords}
                  pageSize={limit}
                  onChange={(p) => setPage(p)}
                  size="small"
                />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default EnquiryActivityLog;
